// eslint-disable-next-line no-unused-vars
import React, {useState, useRef} from 'react';
import {Turnstile} from '@marsidev/react-turnstile';
import styled from 'styled-components';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import PageContentComponent from '../components/ui/pageContentComponent';
import PageContainerTitleComponent from '../components/ui/container/pageContainerTitleComponent';
import ErrorMessageComponent from '../components/ui/messageErrorComponent';
import {Button} from 'react-bootstrap';
import TermsPage from '../pages/TermsPage';
import {useLocation, useNavigate} from 'react-router-dom';
import {FaUser, FaEnvelope, FaLock} from 'react-icons/fa';
import {registerUser} from '../api/axios';
import {useTranslation} from 'react-i18next';
import i18n from '../i18n';

const schema = yup.object().shape({
  username: yup
    .string()
    .min(5, i18n.t('registerPage.usernameMin'))
    .max(12, i18n.t('registerPage.usernameMax'))
    .required(i18n.t('registerPage.usernameRequired')),
  password: yup
    .string()
    .min(6, i18n.t('registerPage.passwordMin'))
    .max(22, i18n.t('registerPage.passwordMax'))
    .required(i18n.t('registerPage.passwordRequired')),
  email: yup.string().email(i18n.t('registerPage.emailInvalid')).required(i18n.t('registerPage.emailRequired')),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], i18n.t('registerPage.passwordsMustMatch'))
    .required(i18n.t('registerPage.confirmPasswordRequired')),
  turnstileToken: yup.string().required(i18n.t('registerPage.turnstileRequired')),
});

const RegisterContainer = styled.div`
  max-width: 600px;
  margin: auto;
  padding: 50px;
  background-image: url('/assets/ui/container-big-bg.png');
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;

  @media (max-width: 768px) {
    padding: 40px;
  }
`;

const TermsBox = styled.div`
  max-height: 500px;
  overflow-y: auto;
  text-align: left;
  color: #fff;
  font-size: 14px;
  margin-bottom: 15px;
`;

const InputContainer = styled.div`
  position: relative;
  margin-bottom: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 5px;
  width: 100%;
  display: block;
`;

const InputField = styled.input`
  width: 100%;
  border-radius: 5px;
  display: block;
`;

const Icon = styled.div`
  position: absolute;
  left: 12px;
  top: 65%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;

  color: #888;
`;

const CaptchaContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 60px;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    transform: scale(0.85);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;
`;

const StyledButton = styled(Button)`
  display: block;
  margin: auto;
  width: 150px;
  max-width: 250px;
  border: none;
`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const titleRef = useRef(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const termsBoxRef = useRef(null);
  const {t} = useTranslation();

  const {
    register,
    handleSubmit,
    setValue,
    formState: {errors},
  } = useForm({resolver: yupResolver(schema)});

  const handleAcceptTerms = () => {
    if (!hasScrolledToBottom) {
      setShowWarning(true);
      return;
    }
    setAcceptedTerms(true);
    setTimeout(() => {
      titleRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, 100);
  };

  const turnstileRef = useRef(null);

  const onSubmit = async data => {
    try {
      const response = await registerUser(data);

      if (response.data.success) {
        navigate(`?popup=success&msg=${encodeURIComponent(response.data.success)}`);
        setTimeout(() => {
          window.dispatchEvent(new Event('closePopup'));
          setTimeout(() => navigate('/'), 500);
        }, 5000);
      } else {
        navigate(`?popup=error&msg=${encodeURIComponent(response.data.error || t('registerPage.unknownError'))}`);
        // Captcha-Token zurücksetzen
        setValue('turnstileToken', '');
        if (turnstileRef.current) {
          turnstileRef.current.reset();
        }
      }
    } catch (error) {
      navigate(
        `?popup=error&msg=${encodeURIComponent(error.response?.data?.error || t('registerPage.registrationFailed'))}`
      );
      // Captcha-Token zurücksetzen
      setValue('turnstileToken', '');
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
    }
  };

  return (
    <PageContentComponent>
      <div ref={titleRef}>
        <PageContainerTitleComponent title={t('registerPage.title')} />
      </div>
      {!acceptedTerms ? (
        <div>
          <TermsBox
            ref={termsBoxRef}
            onScroll={e => {
              const {scrollTop, scrollHeight, clientHeight} = e.target;
              if (scrollTop + clientHeight >= scrollHeight - 5) {
                setHasScrolledToBottom(true);
              }
            }}
          >
            <TermsPage hidePolicySwitcher={location.pathname === '/register'} />
          </TermsBox>
          {showWarning && <ErrorMessageComponent message={t('registerPage.termsWarning')} />}
          <ButtonGroup>
            <StyledButton onClick={handleAcceptTerms}>{t('registerPage.acceptContinue')}</StyledButton>
            <StyledButton onClick={() => navigate('/')}>{t('registerPage.decline')}</StyledButton>
          </ButtonGroup>
        </div>
      ) : (
        <RegisterContainer>
          <ErrorMessageComponent message={t('registerPage.registerInfo')} />
          <form onSubmit={handleSubmit(onSubmit)}>
            {[
              {
                type: 'text',
                icon: <FaUser />,
                field: 'username',
                hint: t('registerPage.usernameHint'),
              },
              {type: 'email', icon: <FaEnvelope />, field: 'email'},
              {
                type: 'password',
                icon: <FaLock />,
                field: 'password',
                hint: t('registerPage.passwordHint'),
              },
              {type: 'password', icon: <FaLock />, field: 'confirmPassword'},
            ].map(({type, icon, field, hint}) => (
              <InputContainer key={field}>
                <Label>
                  {t(`registerPage.${field}Placeholder`)} {hint && hint}
                </Label>
                <Icon>{icon}</Icon>
                <InputField
                  type={type}
                  placeholder={t(`registerPage.${field}Placeholder`, {
                    field: field.charAt(0).toUpperCase() + field.slice(1),
                  })}
                  {...register(field)}
                  autoComplete="off"
                />
                <div style={{position: 'absolute', top: '100%', left: 0, width: '100%'}}>
                  {errors[field] && <ErrorMessageComponent message={errors[field].message} />}
                </div>
              </InputContainer>
            ))}
            <CaptchaContainer>
              <Turnstile
                ref={turnstileRef}
                siteKey={process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY}
                onSuccess={token => {
                  setValue('turnstileToken', token);
                }}
              />
              {errors.turnstileToken && <ErrorMessageComponent message={errors.turnstileToken.message} />}
            </CaptchaContainer>
            <StyledButton type="submit">{t('registerPage.submitButton')}</StyledButton>
          </form>
        </RegisterContainer>
      )}
    </PageContentComponent>
  );
};

export default RegisterPage;
